import { CopyIdButton, type PanelSpan } from '@/components/shared';
import { formatDateTime, formatShortId, maskEmailAddress } from '../format';
import type { BackofficeUserDetail } from '../types';
import { UserDetailPanel } from './UserDetailPanelGrid';

const PLACEHOLDER = 'Not available yet';

export interface UserDetailsPanelProps {
  user: BackofficeUserDetail;
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
}

function DetailRow({
  label,
  value,
  copyValue,
  copyLabel,
}: {
  label: string;
  value: string;
  copyValue?: string;
  copyLabel?: string;
}) {
  return (
    <div className="grid gap-1 border-b border-neutral-100 py-3 last:border-b-0 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
      <dt className="font-semibold text-neutral-900">{label}</dt>
      <dd className="min-w-0 text-neutral-700">
        {copyValue && copyLabel ? (
          <div className="group inline-flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate">{value}</span>
            <CopyIdButton value={copyValue} label={copyLabel} />
          </div>
        ) : (
          <span>{value}</span>
        )}
      </dd>
    </div>
  );
}

export function UserDetailsPanel({ user, panelId, currentSpan }: UserDetailsPanelProps) {
  const displayName = user.name ?? user.email;
  const detailItems = [
    {
      id: 'id',
      label: 'ID',
      content: (
        <DetailRow
          key="id"
          label="ID"
          value={formatShortId(user.id)}
          copyValue={user.id}
          copyLabel="Copy user ID"
        />
      ),
    },
    {
      id: 'name',
      label: 'Name',
      content: (
        <DetailRow
          key="name"
          label="Name"
          value={displayName}
          copyValue={displayName}
          copyLabel="Copy name"
        />
      ),
    },
    {
      id: 'email',
      label: 'Email',
      content: (
        <DetailRow
          key="email"
          label="Email"
          value={maskEmailAddress(user.email)}
          copyValue={user.email}
          copyLabel="Copy email"
        />
      ),
    },
    {
      id: 'last-seen-online',
      label: 'Last seen online',
      content: (
        <DetailRow
          key="last-seen-online"
          label="Last seen online"
          value={formatDateTime(user.lastSeenOnlineAt)}
        />
      ),
    },
    {
      id: 'created',
      label: 'Created',
      content: <DetailRow key="created" label="Created" value={formatDateTime(user.createdAt)} />,
    },
    {
      id: 'preferred-language',
      label: 'Preferred language',
      content: (
        <DetailRow
          key="preferred-language"
          label="Preferred language"
          value={user.preferredLanguage ?? PLACEHOLDER}
        />
      ),
    },
    {
      id: 'region',
      label: 'Region',
      content: <DetailRow key="region" label="Region" value={user.region ?? PLACEHOLDER} />,
    },
    {
      id: 'timezone',
      label: 'Timezone',
      content: <DetailRow key="timezone" label="Timezone" value={user.timeZone ?? PLACEHOLDER} />,
    },
  ];

  return (
    <UserDetailPanel
      title="User details"
      span="2x3"
      panelId={panelId}
      currentSpan={currentSpan}
      settingsItems={detailItems}
    />
  );
}
